import { ReactNode, useEffect, useMemo, useState } from 'react';

import createSafeContext from '@paradigm/utils/src/createSafeContext';
import { useObservable } from '@paradigm/utils/src/hooks';
import UnifiedTradeTapeService, {
  buildTradeTapeService,
} from '#/unified-rfqs/services/unified-trade-tapes';
import { useRfqFiltersValue } from '#/unified-rfqs/ui/rfq-filters/filter-provider';
import { TradeTapeItem } from '#/unified-rfqs/entities/shared';
import { useGrfqService } from './grfqs';
import { useDrfqService } from './drfqs';

export const [useTradeTapeService, TradeTapeProviderComponent] =
  createSafeContext<UnifiedTradeTapeService>('TradeTapeProvider');

interface Props {
  readonly children: ReactNode;
}

export function UnifiedRfqTradeTapeProvider({ children }: Props) {
  const grfqService = useGrfqService();
  const drfqService = useDrfqService();
  const { state: filterState } = useRfqFiltersValue();
  const { auctionTypes, productCodes, strategyCodes } = filterState;

  const filters = useMemo(
    () => ({ auctionTypes, productCodes, strategyCodes }),
    [auctionTypes, productCodes, strategyCodes],
  );

  const tradeTapeService = useMemo(
    () =>
      buildTradeTapeService({
        grfqService,
        drfqService,
        filters,
        tradeTapeStore: grfqService.tradeTapeStore,
      }),
    [grfqService, drfqService, filters],
  );

  return (
    <TradeTapeProviderComponent
      key={tradeTapeService.id}
      value={tradeTapeService}
    >
      {children}
    </TradeTapeProviderComponent>
  );
}

export function useTradeTapeFetchData() {
  const { fetcher } = useTradeTapeService();
  return useObservable(fetcher.fetchData);
}

export function useTradeTapeIdsList() {
  const { fetcher } = useTradeTapeService();
  const [tradesIds, setTradesIds] = useState(fetcher.selectPaginatedIds());

  useEffect(() => {
    const clearSubscription = fetcher.subscribePaginatedIds(setTradesIds);
    return clearSubscription;
  }, [fetcher]);

  return tradesIds;
}

export function useMaybeTradeTapeItem(tradeId: TradeTapeItem['id']) {
  const tradeTapeService = useTradeTapeService();
  const ob = useMemo(
    () => tradeTapeService.item(tradeId),
    [tradeId, tradeTapeService],
  );
  return useObservable(ob);
}

/**
 * ATTENTION: This hook should be used only in places in which
 * the trade tape item is guaranteed to exist.
 *
 */
export function useTradeTapeItem(tradeId: TradeTapeItem['id']) {
  const trade = useMaybeTradeTapeItem(tradeId);

  if (trade == null) {
    throw new TypeError(`Trade tape item ${tradeId} is null`);
  }

  return trade;
}
